import { Button, Descriptions, Card, Divider, Tag } from "antd";
import { PrinterOutlined } from "@ant-design/icons";
import React, { useRef, useState } from "react";
import axios from "axios";
import moment from "moment";
import { useQuery } from "react-query";
import { useReactToPrint } from "react-to-print";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { serverUrl } from "../../../../../nestserver";
import BackButton from "../../../../../components/buttons/BackButton";
import ApplicationFeeReceipt from "../../../../../components/ApplicationFeeReceipt";
import { selectSchoolId } from "../../../../../redux/slices/auth.slice";
import { formatAcademicYearFromYear } from "../../../../../utils/Const";

const GenderDisplay = ({ value }) => {
  const genderMap = {
    male: "Male",
    female: "Female",
    others: "Other",
  };

  return <span>{genderMap[value] || value}</span>;
};

const BloodGroupDisplay = ({ value }) => {
  return <span>{value || "Not specified"}</span>;
};

const ViewAdmission = () => {
  const { id } = useParams();
  const [admissionData, setAdmissionData] = useState(null);

  // Fetch admission data
  async function fetchAdmissionData() {
    const res = await axios({
      method: "get",
      url: `${serverUrl}/admin/application/${id}`,
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
      },
    });
    return res.data;
  }

  const { isLoading } = useQuery(
    ["admission", id],
    fetchAdmissionData,
    {
      enabled: !!id,
      onSuccess: (data) => {
        setAdmissionData(data);
      },
    }
  );

  // School details feed the printable fee receipt's letterhead.
  const schoolId = useSelector(selectSchoolId);
  const schoolQuery = useQuery(
    ["school", schoolId],
    () =>
      axios
        .get(`${serverUrl}/school/${schoolId}`, {
          headers: {
            Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
          },
        })
        .then((res) => res.data),
    { enabled: !!schoolId }
  );
  const school = schoolQuery.data?.result?.data;
  const receiptRef = useRef();
  const handlePrintReceipt = useReactToPrint({
    contentRef: receiptRef,
    documentTitle: `fee-receipt-${admissionData?.application_number || ""}`,
  });

  const getStatusTag = (status) => {
    const statusConfig = {
      approved: { color: "green", text: "Approved" },
      pending: { color: "orange", text: "Pending" },
      rejected: { color: "red", text: "Rejected" },
      withdraw: { color: "default", text: "Withdrawn" },
    };

    const config = statusConfig[status] || { color: "default", text: status };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  if (isLoading) {
    return <div>Loading admission data...</div>;
  }

  if (!admissionData) {
    return <div>No admission data found</div>;
  }

  const data = admissionData;

  return (
    <div className="p-6">
      {/* Header Section */}
      <div className="form-header mb-6 p-4 bg-gray-50 rounded-lg">
        <div className="flex justify-between items-center">
          <h3 className="text-2xl font-bold">Admission Details</h3>
          <div className="text-right">
            <div className="p-4 text-right">
              <BackButton />
            </div>
            <h4 className="text-lg">
              Application Number:{" "}
              <span className="font-semibold">{data?.application_number}</span>
            </h4>
            <h4 className="text-lg">
              Admission Number:{" "}
              <span className="font-semibold">
                {data?.admission_id?.admission_number || "N/A"}
              </span>
            </h4>
            <div className="mt-2">Status: {getStatusTag(data?.status)}</div>
            {/* Existing students never owe an application fee */}
            {data?.is_existing_student && (
              <div
                className="mt-2 flex items-center justify-end"
                style={{ gap: "0.5rem" }}
              >
                <span className="text-sm">Application Fee:</span>
                <Tag>N/A</Tag>
              </div>
            )}
            {data?.application_fee_status &&
              data?.application_fee_status !== "not_required" && (
                <div
                  className="mt-2 flex items-center justify-end"
                  style={{ gap: "0.5rem" }}
                >
                  <span className="text-sm">
                    Application Fee: ₹{data?.application_fee_amount || 0}
                  </span>
                  <Tag
                    color={
                      data.application_fee_status === "paid"
                        ? "green"
                        : data.application_fee_status === "waived"
                        ? "blue"
                        : "orange"
                    }
                  >
                    {data.application_fee_status === "paid"
                      ? "Paid"
                      : data.application_fee_status === "waived"
                      ? "Waived"
                      : "Pending"}
                  </Tag>
                  {data.application_fee_status === "paid" && (
                    <Button
                      size="small"
                      icon={
                        <PrinterOutlined
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        />
                      }
                      onClick={handlePrintReceipt}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      Print Receipt
                    </Button>
                  )}
                </div>
              )}
          </div>
        </div>
      </div>

      {/* Student Details */}
      <Card title="Student Details" className="mb-6">
        <Descriptions column={2} bordered>
          <Descriptions.Item label="First Name" span={1}>
            {data?.first_name}
          </Descriptions.Item>
          <Descriptions.Item label="Last Name" span={1}>
            {data?.last_name}
          </Descriptions.Item>
          <Descriptions.Item label="Date of Birth" span={1}>
            {data?.dob ? moment(data.dob).format("DD-MM-YYYY") : "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Gender" span={1}>
            <GenderDisplay value={data?.gender} />
          </Descriptions.Item>
          <Descriptions.Item label="Nationality" span={1}>
            {data?.nationality || "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Aadhar" span={1}>
            {data?.aadhar || "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Class" span={1}>
            {data?.class?.name || "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Academic Year" span={1}>
            {formatAcademicYearFromYear(data?.enrollment_year) || "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Mother Tongue" span={1}>
            {data?.mother_tongue || "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Email" span={1}>
            {data?.email || "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Phone" span={1}>
            {data?.phone || "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Birth Place" span={1}>
            {data?.birth_place || "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Category" span={1}>
            {data?.category || "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Religion" span={1}>
            {data?.religion || "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Ethnicity" span={2}>
            {data?.ethnicity || "N/A"}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Health Details */}
      <Card title="Health Details" className="mb-6">
        <Descriptions column={2} bordered>
          <Descriptions.Item label="Blood Group">
            <BloodGroupDisplay value={data?.health_details?.blood_group} />
          </Descriptions.Item>
          <Descriptions.Item label="Disabled">
            {data?.health_details?.is_disabled ? "Yes" : "No"}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Existing Siblings */}
      {data?.existing_siblings && data.existing_siblings.length > 0 && (
        <Card title="Existing Siblings" className="mb-6">
          {data.existing_siblings.map((sibling, index) => (
            <div key={index} className="mb-4 last:mb-0">
              <Descriptions column={2} bordered>
                <Descriptions.Item label="Name">
                  {sibling.name || "N/A"}
                </Descriptions.Item>
                <Descriptions.Item label="Student ID">
                  {sibling.student_id || "N/A"}
                </Descriptions.Item>
              </Descriptions>
              {index < data.existing_siblings.length - 1 && <Divider />}
            </div>
          ))}
        </Card>
      )}

      {/* Previous School */}
      <Card title="Previous School" className="mb-6">
        <Descriptions column={2} bordered>
          <Descriptions.Item label="School Name">
            {data?.previous_school?.name || "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Class Completed">
            {data?.previous_school?.class_completed || "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Overall Grade">
            {data?.previous_school?.grade || "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Year Completed">
            {data?.previous_school?.graduated_year || "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Reference Contact">
            {data?.previous_school?.phone || "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Reason for Change" span={2}>
            {data?.previous_school?.change_reason || "N/A"}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Father's Details */}
      <Card title="Father's Details" className="mb-6">
        <Descriptions column={2} bordered>
          <Descriptions.Item label="Name">
            {[data?.primary_contact?.first_name, data?.primary_contact?.last_name]
              .filter(Boolean)
              .join(" ") || "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Relation">
            {data?.primary_contact?.relation || "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Phone">
            {data?.primary_contact?.contact || "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Email">
            {data?.primary_contact?.email || "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Aadhaar">
            {data?.primary_contact?.aadhar || "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Education">
            {data?.primary_contact?.education || "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Occupation">
            {data?.primary_contact?.occupation || "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Annual Income">
            {data?.primary_contact?.annual_income || "N/A"}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Mother's Details */}
      <Card title="Mother's Details" className="mb-6">
        <Descriptions column={2} bordered>
          <Descriptions.Item label="Name">
            {data?.mother_name || "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Phone">
            {data?.mother_phone || "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Email">
            {data?.mother_email || "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Aadhaar">
            {data?.mother_aadhar || "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Education">
            {data?.mother_education || "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Occupation">
            {data?.mother_occupation || "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Annual Income">
            {data?.mother_annual_income || "N/A"}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Address & Emergency Contact */}
      <Card title="Address & Emergency Contact" className="mb-6">
        <Descriptions column={2} bordered>
          <Descriptions.Item label="Address" span={2}>
            {data?.primary_contact?.address || "N/A"}
          </Descriptions.Item>
          <Descriptions.Item label="Emergency Contact">
            {data?.primary_contact?.emergency_contact || "N/A"}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Secondary Contact */}
      {!data?.is_contacts_matching && (
        <Card title="Secondary Contact" className="mb-6">
          <Descriptions column={2} bordered>
            <Descriptions.Item label="First Name">
              {data?.secondary_contact?.first_name || "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Last Name">
              {data?.secondary_contact?.last_name || "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Relation">
              {data?.secondary_contact?.relation || "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Occupation">
              {data?.secondary_contact?.occupation || "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Education">
              {data?.secondary_contact?.education || "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Annual Income">
              {data?.secondary_contact?.annual_income || "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Phone">
              {data?.secondary_contact?.contact || "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Email">
              {data?.secondary_contact?.email || "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Aadhar">
              {data?.secondary_contact?.aadhar || "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Address" span={2}>
              {data?.secondary_contact?.address || "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Emergency Contact">
              {data?.secondary_contact?.emergency_contact || "N/A"}
            </Descriptions.Item>
          </Descriptions>
        </Card>
      )}

      {/* Transportation */}
      <Card title="Transportation" className="mb-6">
        <Descriptions column={2} bordered>
          <Descriptions.Item label="Transportation Required">
            {data?.is_transportation_required ? "Yes" : "No"}
          </Descriptions.Item>
          {data?.is_transportation_required && (
            <>
              <Descriptions.Item label="Transport Zone">
                {data?.transport_zone_id?.name || "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Destination">
                {data?.transport_destination_id || "N/A"}
              </Descriptions.Item>
            </>
          )}
        </Descriptions>
      </Card>

      {/* Referral */}
      <Card title="Referral" className="mb-6">
        <Descriptions column={2} bordered>
          <Descriptions.Item label="Referral Type">
            {data?.referral?.is_friend
              ? "Friend"
              : data?.referral?.name || "N/A"}
          </Descriptions.Item>
          {data?.referral?.is_friend && (
            <>
              <Descriptions.Item label="Friend Name">
                {data?.referral?.name || "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Friend Email">
                {data?.referral?.email || "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Friend Phone">
                {data?.referral?.phone || "N/A"}
              </Descriptions.Item>
            </>
          )}
        </Descriptions>
      </Card>

      {/* Documents */}
      {data?.documents && data.documents.length > 0 && (
        <Card title="Documents" className="mb-6">
          <ul className="list-disc list-inside">
            {data.documents.map((doc, index) => (
              <li key={index} className="mb-2 capitalize">
                {doc?.files?.name || `Document ${index + 1}`}
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* Hidden printable fee receipt — printed via the header button */}
      {data?.application_fee_status === "paid" && (
        <div style={{ display: "none" }}>
          <ApplicationFeeReceipt
            ref={receiptRef}
            school={school}
            applicationNumber={data?.application_number}
            studentName={[data?.first_name, data?.last_name]
              .filter(Boolean)
              .join(" ")}
            amount={data?.application_fee_amount}
            paymentDate={data?.application_fee_recorded_at}
          />
        </div>
      )}
    </div>
  );
};

export default ViewAdmission;
